<template>
  <floating-element
    v-if="item != null"
    ref="el"
    class="scheduledimpulse-createline"
    :x="x"
    :y="y"
    transition-name="transition-from-left-corner"
    teleport-to="#app"
    @close="close"
    @pointerdown:outside="close"
  >
    <form
      ref="form"
      class="scheduledimpulse-createline__form"
      @submit.prevent="onSubmit()"
    >
      <input-number
        :label="roundName"
        :value="state.roundIndex + 1"
        :min="minRound"
        :max="simulation?.maxRounds"
        @update:value="state.roundIndex = $event - 1"
      />

      <expression-input
        ref="expressionInput"
        v-model:expression="state.amount"
        :label="$t('objectNames.effect')"
        :autofocus="true"
        :parse-context="{
          targets,
          resources,
          indices,
          rounds: closestEvaluatedRound ? [closestEvaluatedRound] : [],
          affectedType: item.type,
          affectedId: item.id
        }"
        default-sign="+"
      />

      <button
        class="button pv-1"
        type="submit"
        :disabled="state.amount == '' || state.amount == '0' || state.amount == '+0' || state.amount == '-0'"
      >
        {{ $t("actions.createImpulse") }}
      </button>
    </form>
  </floating-element>
</template>

<script setup lang="ts">
import { reactive, computed, watch, ref, nextTick } from "vue";
import { useI18n } from "vue-i18n";

import FloatingElement from "@core/ui/FloatingElement.vue";
import InputNumber from "@core/input/InputNumber.vue";
import ExpressionInput from "@editor/items/expression/ExpressionInput.vue";

import Simulation from "@editor/items/simulation/Simulation";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useSimulationStore } from "@editor/items/simulation/simulations.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { useIndexStore } from "../index/indices.store";

const props = defineProps<{
  item?: { type: "target" | "resource"; id: number };
  x: number;
  y: number;
}>();

const emit = defineEmits<{
  (e: "create", effect: any): void;
  (e: "close"): void;
}>();

const state = reactive({
  roundIndex: 0,
  amount: ""
});

const expressionInput = ref<InstanceType<typeof ExpressionInput>>();
const el = ref<HTMLElement>();

const { t } = useI18n();
const simulationViewStore = useSimulationViewStore();
const selectionStore = useSelectionStore();

const simulation = computed(() => simulationViewStore.simulation);
const targets = computed(() => simulationViewStore.targets);
const resources = computed(() => simulationViewStore.resources);

const minRound = computed(() => {
  return Math.max(simulationViewStore.currentRoundIndex + 1, 1);
});

const roundName = computed(() => {
  if (simulation.value == null) return "";
  const fixedRoundName = useSettingsStore().simulationRoundName;
  if (fixedRoundName && simulation.value.roundUnit === "round") {
    return fixedRoundName;
  }
  return t(`roundUnits.${simulation.value.roundUnit}`);
});

const closestEvaluatedRound = computed(() => {
  if (simulation.value == null) return;
  let index = state.roundIndex;
  const currentRoundIndex = simulationViewStore.currentRoundIndex;
  if (index > currentRoundIndex) {
    index = currentRoundIndex;
  }
  if (!simulation.value.complete) {
    index -= 1;
  }
  if (index < 0) {
    const sim = new Simulation(simulation.value);
    return sim.virtualGameStartRound;
  }
  return simulation.value.rounds[index];
});

const indices = computed(() => {
  return useIndexStore().items;
});

watch(
  () => props.item,
  (item) => {
    if (item != null) {
      selectionStore.addHighlight(item);
      state.roundIndex = Math.max(simulationViewStore.selectedRoundIndex ?? 0, 0);

      nextTick(() => {
        const pointsInput = expressionInput.value;
        if (!pointsInput) return;
        pointsInput.focus();
      });
    }
  },
  { immediate: true }
);

function onSubmit() {
  if (simulation.value == null) {
    throw new Error("No simulation");
  }
  if (!props.item) {
    throw new Error("No item");
  }
  const effect = {
    affectedType: props.item.type,
    affectedId: props.item.id,
    impulseExpressions: [state.amount],
    simulationId: simulation.value.id,
    roundIndex: state.roundIndex
  };
  useSimulationStore().addScheduledEffect(effect);
  emit("create", effect);
  close();
}

function close() {
  if (props.item) {
    selectionStore.removeHighlight(props.item);
  }
  emit("close");
}
</script>

<style lang="scss">
.scheduledimpulse-createline {
  position: fixed;
  top: 0;
  left: 0;
  padding: 1rem;
  background-color: var(--color-background-deep);
  box-shadow: 0 2px 10px var(--color-shadow-2);
  border-radius: 5px;

  &__form {
    display: grid;
    grid-template-columns: minmax(4rem, min-content) minmax(7.5rem, min-content) min-content;
    gap: 1rem;
    align-items: flex-end;
  }
}
</style>
